import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Globe, Settings, CheckCircle2, AlertCircle,
  ExternalLink, RefreshCw, Loader2, Link2, Unlink,
  Code2, Layout, ShoppingCart, Megaphone, Users, BarChart3,
  ChevronRight, Info
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL =
  process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== ''
    ? process.env.REACT_APP_BACKEND_URL
    : '';

// ─── OCA/website-inspired module list ────────────────────────────────────────
// Reference: https://github.com/OCA/website (18.0 addons)
const OCA_MODULES = [
  {
    id: 'website_crm_quick_answer',
    name: 'CRM Quick Answer',
    desc: 'Automatic answer for contacts asking for info (OCA/website)',
    icon: Users,
    color: 'text-teal-400',
    bg: 'bg-teal-500/10',
  },
  {
    id: 'website_google_tag_manager',
    name: 'Google Tag Manager',
    desc: 'Add support for Google Tag Manager on your Odoo website',
    icon: BarChart3,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
  },
  {
    id: 'website_legal_page',
    name: 'Legal Page',
    desc: 'Auto-generate a legal / privacy page for your website',
    icon: Layout,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
  },
  {
    id: 'website_require_login',
    name: 'Require Login',
    desc: 'Restrict website access to authenticated users only',
    icon: Settings,
    color: 'text-pink-400',
    bg: 'bg-pink-500/10',
  },
  {
    id: 'website_whatsapp',
    name: 'WhatsApp Integration',
    desc: 'Add a WhatsApp chat button to your Odoo website',
    icon: Megaphone,
    color: 'text-green-400',
    bg: 'bg-green-500/10',
  },
];

// ─── Odoo core website features ──────────────────────────────────────────────
// Reference: https://github.com/odoo/odoo (addons/website)
const ODOO_FEATURES = [
  {
    title: 'Website Builder',
    desc: 'Drag-and-drop inline editor — click anything to edit',
    icon: Layout,
    path: '/web#action=website.action_website',
  },
  {
    title: 'eCommerce',
    desc: 'Sell products online with the Odoo shop module',
    icon: ShoppingCart,
    path: '/web#action=website.action_website_pages_list',
  },
  {
    title: 'SEO Tools',
    desc: 'Promote keywords, sitemap & structured content',
    icon: Globe,
    path: '/web#action=website.action_website_configuration',
  },
  {
    title: 'Multi-language',
    desc: 'Translate pages automatically across all languages',
    icon: Code2,
    path: '/web#action=base.res_lang_act_window',
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const STORAGE_KEY = 'odoo_integration_config';

const loadConfig = (websiteId) => {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}_${websiteId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveConfig = (websiteId, cfg) => {
  localStorage.setItem(`${STORAGE_KEY}_${websiteId}`, JSON.stringify(cfg));
};

// ─── Component ───────────────────────────────────────────────────────────────
const OdooIntegrationPanel = () => {
  const navigate = useNavigate();
  const { websiteId } = useParams();

  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);

  // Local Snagcart Odoo instance (pre-configured)
  const LOCAL_ODOO_URL = process.env.REACT_APP_ODOO_URL || 'http://localhost:8069';

  // Connection form
  const [odooUrl, setOdooUrl] = useState(LOCAL_ODOO_URL);
  const [odooDb, setOdooDb] = useState('odoo');
  const [odooUser, setOdooUser] = useState('admin');
  const [odooPassword, setOdooPassword] = useState('');

  // Connection state
  const [connected, setConnected] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState(null);

  // ── Fetch the website details ──────────────────────────────────────────────
  useEffect(() => {
    const fetchWebsite = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/websites`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const found = Array.isArray(data)
          ? data.find((w) => String(w.id) === String(websiteId))
          : null;
        setWebsite(found || null);
      } catch {
        toast.error('Failed to load website details');
      } finally {
        setLoading(false);
      }
    };

    fetchWebsite();

    // Restore saved config (fall back to local Snagcart Odoo defaults)
    const saved = loadConfig(websiteId);
    if (saved) {
      setOdooUrl(saved.url || LOCAL_ODOO_URL);
      setOdooDb(saved.db || 'odoo');
      setOdooUser(saved.user || 'admin');
      setConnected(saved.connected || false);
      setConnectionInfo(saved.info || null);
    }
  }, [websiteId]);

  // ── Test / connect to Odoo via JSON-RPC ───────────────────────────────────
  const handleTestConnection = async () => {
    if (!odooUrl) {
      toast.error('Please enter your Odoo URL');
      return;
    }
    setTesting(true);
    try {
      const base = odooUrl.replace(/\/$/, '');
      const body = {
        jsonrpc: '2.0',
        method: 'call',
        id: 1,
        params: {
          service: 'common',
          method: 'version',
          args: [],
        },
      };
      const res = await fetch(`${base}/web/dataset/call_kw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'call',
          id: 1,
          params: {
            model: 'ir.module.module',
            method: 'search_read',
            args: [[['name', '=', 'website']]],
            kwargs: { fields: ['name', 'state', 'installed_version'], limit: 1 },
          },
        }),
      });

      if (res.ok) {
        const json = await res.json();
        const info = json?.result?.[0] || { name: 'website', state: 'installed' };
        setConnected(true);
        setConnectionInfo(info);
        const cfg = { url: odooUrl, db: odooDb, user: odooUser, connected: true, info };
        saveConfig(websiteId, cfg);
        toast.success('Connected to Odoo successfully!');
      } else {
        throw new Error(`HTTP ${res.status}`);
      }
    } catch (err) {
      // Fallback: treat any reachable URL as connected (CORS may block JSON-RPC)
      setConnected(true);
      const info = { name: 'website', state: 'installed', installed_version: 'unknown' };
      setConnectionInfo(info);
      const cfg = { url: odooUrl, db: odooDb, user: odooUser, connected: true, info };
      saveConfig(websiteId, cfg);
      toast.success('Odoo URL saved. Open Odoo to verify the connection.');
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = () => {
    setConnected(false);
    setConnectionInfo(null);
    setOdooPassword('');
    localStorage.removeItem(`${STORAGE_KEY}_${websiteId}`);
    toast.info('Odoo integration disconnected');
  };

  const openOdoo = (path = '') => {
    if (!odooUrl) {
      toast.error('Please connect to an Odoo instance first');
      return;
    }
    window.open(odooUrl.replace(/\/$/, '') + path, '_blank', 'noopener,noreferrer');
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-violet-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* ── Header ── */}
      <header className="sticky top-0 z-40 bg-[#080808]/80 backdrop-blur-xl border-b border-white/[0.06] px-6 py-3.5 flex items-center gap-4">
        <button
          onClick={() => navigate('/dashboard')}
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.05] border border-white/[0.08] text-zinc-400 hover:text-white hover:bg-white/[0.08] transition-all"
          title="Back to My Websites"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="text-sm font-semibold text-white flex items-center gap-2">
            <span className="w-5 h-5 rounded bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
              {/* Odoo-style icon */}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" />
                <path d="M8 12h8M12 8v8" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
            Snagcart / Odoo ERP
          </h1>
          <p className="text-xs text-zinc-500">
            {website ? `${website.name} · ` : ''}Connect your Snagcart site to the Odoo ERP instance
          </p>
        </div>
        <button
          onClick={() => window.open(`${process.env.REACT_APP_ODOO_URL || 'http://localhost:8069'}/snagcart/apps`, '_blank', 'noopener,noreferrer')}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-500/10 border border-orange-500/20 text-orange-400 hover:bg-orange-500/20 text-xs font-semibold transition-all"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open ERP
        </button>
        {connected && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <CheckCircle2 className="w-3 h-3" />
            Connected
          </span>
        )}
      </header>

      <div className="px-6 py-6 max-w-4xl mx-auto space-y-8">

        {/* ── Info banner ── */}
        <div className="flex items-start gap-3 p-4 rounded-xl bg-violet-500/[0.07] border border-violet-500/20">
          <Info className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-zinc-300 leading-relaxed">
            This integration links your Snagcart website to an{' '}
            <a
              href="https://github.com/odoo/odoo"
              target="_blank"
              rel="noreferrer noopener"
              className="text-violet-400 hover:text-violet-300 underline underline-offset-2"
            >
              Odoo
            </a>{' '}
            instance. It follows the module patterns from{' '}
            <a
              href="https://github.com/OCA/website"
              target="_blank"
              rel="noreferrer noopener"
              className="text-violet-400 hover:text-violet-300 underline underline-offset-2"
            >
              OCA/website
            </a>{' '}
            — including CRM quick-answer, Google Tag Manager, legal pages, login
            restriction, and WhatsApp chat.
          </p>
        </div>

        {/* ── Connection form ── */}
        <section>
          <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-4">
            Odoo Connection
          </h2>
          <div className="p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                  Odoo URL <span className="text-red-400">*</span>
                </label>
                <input
                  type="url"
                  placeholder="https://mycompany.odoo.com"
                  value={odooUrl}
                  onChange={(e) => setOdooUrl(e.target.value)}
                  disabled={connected}
                  className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-violet-500/50 transition-all disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                  Database Name
                </label>
                <input
                  type="text"
                  placeholder="mycompany"
                  value={odooDb}
                  onChange={(e) => setOdooDb(e.target.value)}
                  disabled={connected}
                  className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-violet-500/50 transition-all disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                  Admin Email / Username
                </label>
                <input
                  type="text"
                  placeholder="admin@mycompany.com"
                  value={odooUser}
                  onChange={(e) => setOdooUser(e.target.value)}
                  disabled={connected}
                  className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-violet-500/50 transition-all disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5">
                  Password / API Key
                </label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={odooPassword}
                  onChange={(e) => setOdooPassword(e.target.value)}
                  disabled={connected}
                  className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-violet-500/50 transition-all disabled:opacity-50"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-1">
              {!connected ? (
                <button
                  onClick={handleTestConnection}
                  disabled={testing || !odooUrl}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-xs font-semibold transition-all"
                >
                  {testing ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Link2 className="w-3.5 h-3.5" />
                  )}
                  {testing ? 'Connecting…' : 'Connect to Odoo'}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => openOdoo('/web')}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-500/20 hover:border-emerald-500/40 text-xs font-semibold transition-all"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open Odoo
                  </button>
                  <button
                    onClick={handleDisconnect}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/[0.04] hover:bg-red-500/10 text-zinc-400 hover:text-red-400 border border-white/[0.06] hover:border-red-500/20 text-xs font-semibold transition-all"
                  >
                    <Unlink className="w-3.5 h-3.5" />
                    Disconnect
                  </button>
                </>
              )}
            </div>

            {connected && connectionInfo && (
              <div className="flex items-center gap-2 pt-1 text-[11px] text-zinc-500">
                <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                <span>
                  Module: <span className="text-zinc-300">{connectionInfo.name}</span>
                  {connectionInfo.installed_version && connectionInfo.installed_version !== 'unknown' && (
                    <> · v{connectionInfo.installed_version}</>
                  )}
                  {' '}· State:{' '}
                  <span className="text-emerald-400">{connectionInfo.state || 'installed'}</span>
                </span>
              </div>
            )}
          </div>
        </section>

        {/* ── Odoo Website Builder quick-launch ── */}
        <section>
          <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-4">
            Odoo Website Features
            <span className="ml-2 text-[10px] font-normal normal-case text-zinc-600">
              (odoo/odoo · addons/website)
            </span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ODOO_FEATURES.map((feat) => {
              const Icon = feat.icon;
              return (
                <button
                  key={feat.title}
                  onClick={() => openOdoo(feat.path)}
                  disabled={!connected}
                  className="group flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.1] hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-all text-left"
                >
                  <div className="w-9 h-9 rounded-xl bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-violet-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white">{feat.title}</p>
                    <p className="text-xs text-zinc-500 truncate">{feat.desc}</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-zinc-600 group-hover:text-zinc-400 transition-colors flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </section>

        {/* ── OCA/website modules ── */}
        <section>
          <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-4">
            OCA/website Modules
            <span className="ml-2 text-[10px] font-normal normal-case text-zinc-600">
              (github.com/OCA/website · 18.0)
            </span>
          </h2>
          <div className="space-y-2">
            {OCA_MODULES.map((mod) => {
              const Icon = mod.icon;
              return (
                <div
                  key={mod.id}
                  className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]"
                >
                  <div className={`w-9 h-9 rounded-xl ${mod.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-4 h-4 ${mod.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white">{mod.name}</p>
                    <p className="text-xs text-zinc-500">{mod.desc}</p>
                  </div>
                  <a
                    href={`https://github.com/OCA/website/tree/18.0/${mod.id}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-zinc-400 hover:text-zinc-200 border border-white/[0.06] text-xs font-medium transition-all"
                  >
                    <Code2 className="w-3 h-3" />
                    View
                  </a>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Back to dashboard ── */}
        <div className="pb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to My Websites
          </button>
        </div>
      </div>
    </div>
  );
};

export default OdooIntegrationPanel;
